// =========================================================================
// Demo view → route mapping. Fork-owned (zone C).
//
// The source demo navigates with 269 inline `onclick="show('<view>','<sub>')"`
// strings plus one `eval`. Every one of those pairs resolves through this
// single table, so a ported scene never hand-writes a route and a route change
// is edited in exactly one place.
//
// The 32 views were enumerated from the demo's own markup:
//   - `id="v-<view>"` section ids         (31 sections)
//   - `data-view="<view>"` nav attributes (23 values)
//   - `grep -o "show('...')"`              (32 distinct view names)
// `workflows` appears only as a `show()` target, never as a section id; it is
// the workflows section landing.
//
// Anything not in the table returns `null`. Callers decide what that means —
// the palette skips the row, a scene falls back to the section landing. It
// never silently resolves to `/`.
// =========================================================================

import { currentDemoMode } from './DemoModeContext';

/** Every view name the demo can `show()`. */
export type OemView =
  | 'agents'
  | 'alerts'
  | 'approvals'
  | 'asset360'
  | 'connectors'
  | 'costs'
  | 'customers'
  | 'data'
  | 'drilling'
  | 'fleet'
  | 'help'
  | 'incident'
  | 'issues'
  | 'jobboard'
  | 'jobcards'
  | 'jobrun'
  | 'loadhaul'
  | 'loops'
  | 'machine'
  | 'module'
  | 'overview'
  | 'partsflow'
  | 'population'
  | 'remote'
  | 'reports'
  | 'section'
  | 'sites'
  | 'sos'
  | 'surface'
  | 'techs'
  | 'wf'
  | 'workflows';

/**
 * The demo's eight section ids, targets of `show('section', '<id>')`.
 *
 * `home` is not in the table: it depends on the demo mode (see
 * {@link homeLanding}).
 */
const SECTION_LANDING: Record<string, string> = {
  issues: '/issues',
  workflows: '/workflows',
  loops: '/loops',
  assets: '/assets',
  sites: '/sites',
  integration: '/integration',
  help: '/help',
};

/** `show('help', '<sub>')` — the demo's four help pages. */
const HELP_SUB: Record<string, string> = {
  map: '/help/platform-map',
  structure: '/help/structure', // fork(oem/v1.8)
  nav: '/help/navigation',
  roles: '/help/roles',
  // The 17-stop guided tour lives on the help index (`TourScene`).
  tour: '/help',
};

/** `show('partsflow', '<sub>')` — three tabs on one scene. */
const PARTSFLOW_SUB: Record<string, string> = {
  bom: '/parts-flow?tab=bom',
  inventory: '/parts-flow?tab=inventory',
  predict: '/parts-flow?tab=predict',
};

/**
 * `show('sites', '<sub>')` carries two different kinds of sub. Six are mine
 * codes; `conditions` and `parts` are cross-site tabs on the map scene.
 */
const SITE_CODES = new Set(['KG', 'BR', 'MI', 'OH', 'GD', 'MR']);

/** Asset divisions — `show('surface'|'drilling'|'loadhaul', '<subclass>')`. */
const DIVISION_ROUTE: Record<string, string> = {
  surface: '/assets/surface',
  drilling: '/assets/drilling',
  loadhaul: '/assets/loadhaul',
};

/** Views with no sub, mapping one-to-one onto a route. */
const SIMPLE_VIEW: Partial<Record<OemView, string>> = {
  asset360: '/',
  overview: '/sites',
  issues: '/issues',
  approvals: '/approvals',
  costs: '/costs',
  fleet: '/fleet',
  customers: '/customers',
  jobboard: '/job-board',
  jobcards: '/job-cards',
  partsflow: '/parts-flow',
  population: '/population',
  techs: '/technicians',
  remote: '/remote-support',
  sos: '/reports/sos',
  reports: '/reports',
  workflows: '/workflows',
  connectors: '/connectors',
  agents: '/agents',
};

function seg(value: string): string {
  return encodeURIComponent(value);
}

/**
 * `show('section','home')`. The Home section landing (8 tiles and 4 figures)
 * is a Full-mode page. In Executive mode, Home means Asset360, because that is
 * the page the room sees first.
 */
function homeLanding(): string {
  return currentDemoMode() === 'full' ? '/home' : '/';
}

// ---- `show('loops')` with no id --------------------------------------------

/**
 * The demo's `LOOP_CUR` (source line 3030). `show('loops')` with no id
 * reopened whichever loop was last on screen, starting at `learning`.
 *
 * Kept in memory only. It lasts one page session, and a fresh load always
 * starts from the same loop, so snapshots stay the same between runs.
 */
export const DEFAULT_LOOP = 'learning';
let lastLoop: string = DEFAULT_LOOP;

/** Record the loop now on screen. `LoopScene` calls this for a known loop id. */
export function rememberLoop(id: string): void {
  lastLoop = id;
}

/**
 * Resolve one demo `show(view, sub?)` call to an app route.
 *
 * @returns the route, or `null` when the pair has no ported destination.
 *
 * @example routeForView('machine', 'L-02')      // '/machines/L-02'
 * @example routeForView('wf', 'rca')            // '/workflows/steps/rca'
 * @example routeForView('surface', 'Loaders')   // '/assets/surface?subclass=Loaders'
 * @example routeForView('jobrun', 'job:AL-0915') // '/workflows/case-trace?case=job%3AAL-0915'
 */
export function routeForView(view: string, sub?: string): string | null {
  const s = sub?.trim();

  switch (view) {
    // ---- views whose sub is an entity id -------------------------------
    case 'machine':
      return s ? `/machines/${seg(s)}` : '/fleet';
    case 'alerts':
      return s ? `/issues/alerts/${seg(s)}` : '/issues/alerts';
    case 'incident':
      return s ? `/issues/incidents/${seg(s)}` : '/issues/incidents';
    case 'module':
      return s ? `/workflows/modules/${seg(s)}` : '/workflows';
    case 'wf':
      return s ? `/workflows/steps/${seg(s)}` : '/workflows';
    case 'loops':
      return `/loops/${seg(s || lastLoop)}`;
    case 'jobrun':
      // `j.run || 'job:' + j.id` — a worked-run key or a generated one.
      return s ? `/workflows/case-trace?case=${seg(s)}` : '/workflows/case-trace';
    case 'data':
      return s ? `/integration/data?doc=${seg(s)}` : '/integration/data';
    case 'techs':
      return s ? `/technicians?technician=${seg(s)}` : '/technicians';
    case 'jobcards':
      return s ? `/job-cards?job=${seg(s)}` : '/job-cards';
    case 'remote':
      return s ? `/remote-support?session=${seg(s)}` : '/remote-support';
    case 'customers':
      return s ? `/customers?org=${seg(s)}` : '/customers';
    case 'agents':
      return s ? `/agents?agent=${seg(s)}` : '/agents';

    // ---- views with a small fixed sub vocabulary -----------------------
    case 'section':
      if (!s || s === 'home') return homeLanding();
      return SECTION_LANDING[s] ?? null;
    case 'help':
      return s ? (HELP_SUB[s] ?? '/help') : '/help';
    case 'partsflow':
      return s ? (PARTSFLOW_SUB[s] ?? '/parts-flow') : '/parts-flow';
    case 'sites':
      if (!s) return '/sites';
      return SITE_CODES.has(s.toUpperCase())
        ? `/sites/${s.toUpperCase()}`
        : `/sites?tab=${seg(s)}`;

    // ---- asset divisions, optionally narrowed to a subclass ------------
    case 'surface':
    case 'drilling':
    case 'loadhaul': {
      const base = DIVISION_ROUTE[view];
      return s ? `${base}?subclass=${seg(s)}` : base;
    }

    default:
      return SIMPLE_VIEW[view as OemView] ?? null;
  }
}

/**
 * Convenience for ported markup that still reads as a demo handler string.
 * Parses `show('view','sub')` and resolves it. Returns `null` on a malformed
 * or unknown string rather than throwing, so a bad paste degrades to an inert
 * control instead of a blank screen.
 */
export function routeForHandler(handler: string): string | null {
  const m = handler.match(/show\(\s*'([^']+)'\s*(?:,\s*'([^']*)'\s*)?\)/);
  if (!m) return null;
  return routeForView(m[1], m[2]);
}
